// checkout/steps/billingAddressSection.ts

import { checkoutData } from '../init';
import { addressesMatch } from '../utils/address';
import { initHiddenRows, toggleFormRow } from '../utils/toggleFormRows';
import { NotificationService, NotificationType } from '../../utils/NotificationService';
import { toggleClass } from '../../utils/toggleClass';
import { initBillingLoqateLookup } from '../utils/initDeliveryLoqateLookup';

const wrapper_id = 'paymentMethodEditBillingAddress__wrapper';
const container_id = 'paymentMethodEditBillingAddress';
const toggle_link_id = 'billing-address-change';
const form_selector = '#new-address-form';

export const BILLING_CHANGE_EVENT = 'billing:change-open';

/**
 * Initialise and render the billing address section with default payload.
 * Loads the empty/new form into the container.
 * @returns {void}
 */
export function initialiseBillingSection(): void {
	const container = document.getElementById(container_id);
	if (!container) return;

	const url = `${(window as any).serverData?.contextPath || ''}/checkout/address/assignBillingAddress`;
	const body = new URLSearchParams({
		addressTarget: 'billing',
		newAddress: 'true',
		groupIdx: '-1',
		addressIdx: '',
		packIdx: '',
		idx: '',
		packNo: '',
	});

	postBillingForm(url, body)
		.then((res) => handleBillingResponse(container, res.html, res.payload, true))
		.catch((err) => console.error('[billing] initial load failed', err));
}

/**
 * Attach listener to the external “Save billing address” continueBtn.
 * Submits the currently rendered form and handles the response.
 * @returns {void}
 */
export function billingSubmitListener(): void {
	const container = document.getElementById(container_id);
	const continueBtn = document.getElementById(
		'checkout-change-billing-address',
	) as HTMLButtonElement | null;
	const cancelBtn = document.getElementById(
		'checkout-cancel-billing-address',
	) as HTMLButtonElement | null;
	if (!container || !continueBtn || !cancelBtn) return;

	continueBtn.addEventListener('click', async (e) => {
		e.preventDefault();
		const form = container.querySelector<HTMLFormElement>(form_selector);
		if (!form) return;

		const url = `${(window as any).serverData?.contextPath || ''}/checkout/address/assignBillingAddress`;
		const body = new URLSearchParams(new FormData(form) as any);

		try {
			continueBtn.setAttribute('disabled', 'true');
			toggleClass([continueBtn], ['btn-loading']);
			const res = await postBillingForm(url, body);
			handleBillingResponse(container, res.html, res.payload);
		} catch (err) {
			console.error('[billing] submit failed', err);
		} finally {
			continueBtn.removeAttribute('disabled');
			toggleClass([continueBtn], ['btn-loading']);
		}
	});

	cancelBtn.addEventListener('click', () => {
		toggleBillingVisibility(false);
	});
}

/**
 * Attach toggle listeners to the “Change billing address” link.
 * Includes keyboard support (Enter / Space).
 * @returns {void}
 */
export function attachBillingToggle(): void {
	const wrapper = document.getElementById(wrapper_id);
	const link = document.getElementById(toggle_link_id);
	if (!wrapper || !link) return;

	// Ensure wrapper is an accessible region
	if (!wrapper.hasAttribute('role')) wrapper.setAttribute('role', 'region');

	const toggle = (e: Event) => {
		e.preventDefault();
		toggleBillingVisibility(true);

		document.dispatchEvent(new CustomEvent(BILLING_CHANGE_EVENT));
	};

	link.addEventListener('click', toggle);
	link.addEventListener('keydown', (e) => {
		if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') toggle(e);
	});
}

/**
 * Check whether the billing address edit form is currently visible.
 *
 * @returns {boolean} True if the billing edit form is open (not hidden), otherwise false.
 */
export function isBillingEditOpen(): boolean {
	const wrapper = document.getElementById('paymentMethodEditBillingAddress__wrapper');
	return !!(wrapper && !wrapper.hasAttribute('hidden'));
}

/**
 * Close the billing address edit form if it is currently open.
 * This restores the summary view and re-enables the “Change billing address” link.
 *
 * @returns {void}
 */
export function cancelBillingEditIfOpen(): void {
	if (isBillingEditOpen()) {
		toggleBillingVisibility(false);
	}
}

/**
 * Show or hide the billing address form and toggle link.
 * Moves focus appropriately.
 * @param {boolean} show - Whether to show the form.
 * @returns {void}
 */
function toggleBillingVisibility(show: boolean): void {
	const wrapper = document.getElementById(wrapper_id);
	const link = document.getElementById(toggle_link_id);
	const continueBtn = document.querySelector(
		'#checkout-continue-payment-details',
	) as HTMLButtonElement | null;

	if (!wrapper || !link || !continueBtn) return;

	if (show) {
		wrapper.removeAttribute('hidden');
		wrapper.setAttribute('aria-hidden', 'false');

		// hide the toggle while editing
		link.setAttribute('hidden', '');
		link.setAttribute('aria-hidden', 'true');
		link.setAttribute('aria-expanded', 'true');

		// focus first tabbable
		wrapper.querySelector<HTMLElement>('input,select,textarea,button,[tabindex]')?.focus();

		// continueBtn element may be re-rendering on page, so putting hidden attribute change in a timeout.
		setTimeout(function () {
			continueBtn.hidden = true;
		}, 1);
	} else {
		wrapper.setAttribute('hidden', '');
		wrapper.setAttribute('aria-hidden', 'true');

		// show the toggle again
		link.removeAttribute('hidden');
		link.setAttribute('aria-hidden', 'false');
		link.setAttribute('aria-expanded', 'false');

		showBillingAddress();

		link.focus();
		continueBtn.hidden = false;
	}
}

/**
 * Injects the returned billing form HTML, applies status handling,
 * reinitialises interactions, and ensures manual address fields remain visible
 * when validation errors occur.
 *
 * @param {HTMLElement} container - The billing form container element.
 * @param {string} html - HTML returned from the server.
 * @param {any} [payload] - Optional JSON payload from the server.
 * @param {boolean} [stripErrors=false] - Remove server-rendered errors on initial load.
 * @returns {void}
 */
function handleBillingResponse(
	container: HTMLElement,
	html: string,
	payload?: any,
	stripErrors = false,
): void {
	if (!html) return;

	container.innerHTML = html;

	if (stripErrors) {
		container.querySelectorAll('.error-message').forEach((el) => el.remove());
	}

	const status = payload?.status;
	if (status === 'error' || status === 'success') {
		container.setAttribute('data-status', status);
	} else {
		container.removeAttribute('data-status');
	}

	if (status === 'success') {
		toggleBillingVisibility(false);
		const fields = extractAddressFields(html);
		updateBillingAddressSummary(fields);
		showBillingAddress(true);
	}

	if (status === 'error' && !stripErrors) {
		const existing = document.querySelector<HTMLElement>('wc-notification');
		if (existing) existing.remove();

		NotificationService.showNotification(
			NotificationType.error,
			'Error',
			'Please correct the fields in error',
		);
	}

	// Re-bind hidden row controls for dynamically injected markup
	initHiddenRows(container);

	// Rewire manual "Enter address manually" toggle
	const manualToggle = container.querySelector<HTMLElement>('.manual-address-entry');
	if (manualToggle) {
		manualToggle.addEventListener('click', (e) => {
			e.preventDefault();
			toggleFormRow(manualToggle);
		});
		manualToggle.addEventListener('keydown', (e) => {
			if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
				e.preventDefault();
				toggleFormRow(manualToggle);
			}
		});
	}

	// Reinitialise Loqate lookup for the newly-rendered billing form
	initBillingLoqateLookup({ limit: 10, isDelivery: false });

	// Ensure manual address fields are exposed when validation errors occur
	if (status === 'error' && manualToggle && !stripErrors) {
		const targetClass = manualToggle.getAttribute('data-form-target');
		if (targetClass) {
			const manualRow = container.querySelector<HTMLElement>(`.${targetClass}`);
			if (manualRow && manualRow.classList.contains('form__row--hidden')) {
				toggleFormRow(manualToggle);
			}
		}
	}
}

/**
 * POST to the billing address endpoint and return HTML plus JSON payload when present.
 * @param {string} url - Endpoint URL.
 * @param {URLSearchParams} body - Form data to send.
 * @returns {Promise<{ html: string; payload?: unknown }>}
 */
function postBillingForm(
	url: string,
	body: URLSearchParams,
): Promise<{ html: string; payload?: any }> {
	const headers: Record<string, string> = {
		'Content-Type': 'application/x-www-form-urlencoded',
		'X-Requested-With': 'XMLHttpRequest',
		'Accept': 'application/json, text/html;q=0.9,*/*;q=0.8',
	};

	const csrfHeader = document.querySelector<HTMLMetaElement>(
		'meta[name="_csrf_header"]',
	)?.content;
	const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="_csrf"]')?.content;
	if (csrfHeader && csrfToken) headers[csrfHeader] = csrfToken;

	return fetch(url, {
		method: 'POST',
		headers,
		body: body.toString(),
		credentials: 'same-origin',
	}).then(async (res) => {
		if (res.status === 401) {
			window.location.reload();
			throw new Error('Unauthorised (401): reloading page');
		}

		const ct = res.headers.get('Content-Type') || '';
		if (ct.includes('application/json')) {
			const payload = await res.json();
			return { html: getFormHtml(payload), payload };
		}

		return { html: await res.text() };
	});
}

/**
 * Get `viewHtml` from the payload.
 * @param {any} payload - JSON from server.
 * @returns {string}
 */
function getFormHtml(payload: any): string {
	if (!payload) return '';
	if (Array.isArray(payload)) {
		for (const item of payload) {
			if (item?.viewHtml) return item.viewHtml;
		}
		return '';
	}
	return typeof payload.viewHtml === 'string' ? payload.viewHtml : '';
}

/**
 * Toggle “same vs different” billing address UI.
 * If `forceShowDiff` is true, skips hash comparison and always shows the diff.
 *
 * @param {boolean} [forceShowDiff] - Force showing the diff block.
 * @returns {void}
 */
export function showBillingAddress(forceShowDiff?: boolean): void {
	const sameEl = document.getElementById('billing-address-same');
	const diffEl = document.getElementById('billing-address-diff');
	if (!sameEl || !diffEl) return;

	const same = forceShowDiff
		? false
		: addressesMatch(
				checkoutData?.deliveryAddress?.fields?.['deliveryAddress.address.hashCode'],
				checkoutData?.paymentMethod?.fields?.['paymentMethod.address.hashCode'],
			);

	sameEl.toggleAttribute('hidden', !same);
	sameEl.setAttribute('aria-hidden', String(!same));
	diffEl.toggleAttribute('hidden', same);
	diffEl.setAttribute('aria-hidden', String(same));
}

/**
 * Extract all form input field values from returned HTML.
 * @param {string} html - HTML string returned from server.
 * @returns {Record<string, string>}
 */
function extractAddressFields(html: string): Record<string, string> {
	const parser = new DOMParser();
	const doc = parser.parseFromString(html, 'text/html');
	const inputs = doc.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
		'input[name], select[name], textarea[name]',
	);

	const result: Record<string, string> = {};
	inputs.forEach((el) => {
		result[el.name] = el.value;
	});
	return result;
}

/**
 * Populate the billing address summary with submitted field values.
 * Adds "paymentMethod." prefix to match data-name spans.
 * @param {Record<string, string>} fields - Submitted form fields.
 * @returns {void}
 */
function updateBillingAddressSummary(fields: Record<string, string>): void {
	const container = document.getElementById('billing-address-diff');
	if (!container) return;

	const spans = container.querySelectorAll<HTMLElement>('[data-name]');
	spans.forEach((span) => {
		const keys = span.dataset.name?.split(' ') || [];
		for (const key of keys) {
			const plainKey = key.replace(/^paymentMethod\./, '');
			if (fields.hasOwnProperty(plainKey)) {
				span.textContent = fields[plainKey];
				break;
			}
		}
	});
}
